import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAuditLogs, getUsuarios } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  ClipboardText, CalendarBlank, Funnel, MagnifyingGlass
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';

const MODULOS = [
  { value: 'auth', label: 'Autenticación' },
  { value: 'usuarios', label: 'Usuarios' },
  { value: 'alumnos', label: 'Alumnos' },
  { value: 'asistencia', label: 'Asistencia' },
  { value: 'expediente', label: 'Expediente' },
  { value: 'bitacora', label: 'Bitácora' },
  { value: 'patre', label: 'PATRE' },
  { value: 'alertas', label: 'Alertas' },
  { value: 'reportes', label: 'Reportes' },
  { value: 'ciclos_escolares', label: 'Ciclos Escolares' }
];

const ACCIONES = [
  { value: 'LOGIN', label: 'Inicio de sesión' },
  { value: 'CREATE', label: 'Crear' },
  { value: 'UPDATE', label: 'Actualizar' },
  { value: 'DELETE', label: 'Eliminar' },
  { value: 'BULK_UPLOAD', label: 'Carga masiva' },
  { value: 'EXPORT', label: 'Exportar' },
  { value: 'ATTEND', label: 'Atender' }
];

const AuditoriaPage = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    usuario_id: '',
    modulo: '',
    accion: '',
    matricula: ''
  });

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== 'all') params[key] = value;
      });
      
      const response = await getAuditLogs(params);
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Error al cargar auditoría');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  const fetchUsuarios = useCallback(async () => {
    try {
      const response = await getUsuarios();
      setUsuarios(response.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error fetching usuarios:', error);
      }
    }
  }, []);

  useEffect(() => {
    fetchUsuarios();
  }, [fetchUsuarios]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const getAccionColor = (accion) => {
    const colors = {
      LOGIN: 'bg-blue-100 text-blue-800',
      CREATE: 'bg-green-100 text-green-800',
      UPDATE: 'bg-amber-100 text-amber-800',
      DELETE: 'bg-red-100 text-red-800',
      BULK_UPLOAD: 'bg-purple-100 text-purple-800',
      EXPORT: 'bg-cyan-100 text-cyan-800',
      ATTEND: 'bg-emerald-100 text-emerald-800'
    };
    return colors[accion] || 'bg-gray-100 text-gray-800';
  };

  const clearFilters = () => {
    setFilters({
      usuario_id: '',
      modulo: '',
      accion: '',
      matricula: ''
    });
    setPage(1);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="auditoria-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold">Auditoría</h1>
          <p className="text-muted-foreground">Registro de todas las acciones del sistema</p>
        </div>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
        >
          <Funnel size={16} className="mr-1" />
          Filtros
        </Button>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <Label className="text-xs">Usuario</Label>
                <Select 
                  value={filters.usuario_id} 
                  onValueChange={(v) => { setFilters(p => ({ ...p, usuario_id: v })); setPage(1); }}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {usuarios.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.nombre_completo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Módulo</Label>
                <Select 
                  value={filters.modulo} 
                  onValueChange={(v) => { setFilters(p => ({ ...p, modulo: v })); setPage(1); }}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {MODULOS.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Acción</Label>
                <Select 
                  value={filters.accion} 
                  onValueChange={(v) => { setFilters(p => ({ ...p, accion: v })); setPage(1); }}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {ACCIONES.map(a => (
                      <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Matrícula</Label>
                <Input
                  placeholder="Buscar..."
                  value={filters.matricula}
                  onChange={(e) => { setFilters(p => ({ ...p, matricula: e.target.value })); setPage(1); }}
                  className="h-9"
                />
              </div>
              <div className="flex items-end">
                <Button variant="ghost" size="sm" onClick={clearFilters} className="w-full">
                  Limpiar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card>
        <CardHeader className="py-4">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <ClipboardText size={20} />
            {total} registros encontrados
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="w-full">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-[150px]">Fecha/Hora</TableHead>
                  <TableHead>Usuario</TableHead>
                  <TableHead className="w-[100px]">Rol</TableHead>
                  <TableHead className="w-[100px]">Acción</TableHead>
                  <TableHead className="w-[120px]">Módulo</TableHead>
                  <TableHead>Matrícula</TableHead>
                  <TableHead>Detalles</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="spinner mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No se encontraron registros
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => (
                    <TableRow key={log.id} className="hover:bg-muted/30">
                      <TableCell className="text-sm">
                        {log.fecha && format(new Date(log.fecha), 'dd/MM/yyyy HH:mm:ss')}
                      </TableCell>
                      <TableCell className="font-medium">{log.username}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs capitalize">
                          {log.rol}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={`text-xs ${getAccionColor(log.accion)}`}>
                          {log.accion}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {MODULOS.find(m => m.value === log.modulo)?.label || log.modulo}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {log.matricula || '-'}
                      </TableCell>
                      <TableCell className="text-sm max-w-[200px] truncate">
                        {log.datos_nuevos && Object.keys(log.datos_nuevos).length > 0 
                          ? JSON.stringify(log.datos_nuevos).slice(0, 50) + '...'
                          : '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
          
          {/* Pagination */}
          {total > 50 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <p className="text-sm text-muted-foreground">
                Página {page} de {Math.ceil(total / 50)}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= Math.ceil(total / 50)}
                  onClick={() => setPage(p => p + 1)}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditoriaPage;
